import { useState, useEffect } from 'react';
import { getSettings, updateSettings, testEmail, uploadFavicon, getFileUrl, getDiagnosticaQR, pulisciQROrfani, rimuoviQRPerRange, debugElogio, collegaElogioAzienda, resetEmailTemplates, diagnosticaAziendaElogi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Save, Loader2, Globe, Mail, Send, Euro, CreditCard, Wallet, Brain, FileText, Settings, Link, Upload, Image, QrCode, Trash2, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const ImpostazioniPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [resettingTemplates, setResettingTemplates] = useState(false);
  // Gestione QR
  const [qrDiagnostica, setQrDiagnostica] = useState(null);
  const [loadingDiagnostica, setLoadingDiagnostica] = useState(false);
  const [pulendoOrfani, setPulendoOrfani] = useState(false);
  const [rimuovendoRange, setRimuovendoRange] = useState(false);
  const [qrRangeToRemove, setQrRangeToRemove] = useState({ start: '', end: '' });
  // Debug Elogio
  const [debugElogioId, setDebugElogioId] = useState('');
  const [debugResult, setDebugResult] = useState(null);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [collegandoAzienda, setCollegandoAzienda] = useState(false);
  const [aziendaDaCollegare, setAziendaDaCollegare] = useState('');
  // Diagnostica Azienda
  const [diagnosticaAziendaId, setDiagnosticaAziendaId] = useState('');
  const [diagnosticaAziendaResult, setDiagnosticaAziendaResult] = useState(null);
  const [loadingDiagnosticaAzienda, setLoadingDiagnosticaAzienda] = useState(false);
  const [settings, setSettings] = useState({
    // Generale
    frontend_url: '',
    api_url: '',
    prezzo_base: 150,
    prezzo_premium: 100,
    favicon_file_id: '',
    // OpenAI
    openai_api_key: '',
    // Wix
    wix_bearer_token: '',
    wix_site_id: '',
    wix_collection: 'elogi',
    // Resend Email
    resend_api_key: '',
    email_from: 'noreply@remembermelive.it',
    // Stripe
    stripe_public_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    // PayPal
    paypal_client_id: '',
    paypal_client_secret: '',
    paypal_mode: 'sandbox',
    // Template Email - Registrazione
    email_oggetto_registrazione_azienda: 'Benvenuto su RememberMe Live',
    email_template_registrazione_azienda: '',
    email_oggetto_registrazione_admin: 'Account Admin Creato - RememberMe Live',
    email_template_registrazione_admin: '',
    email_oggetto_registrazione_agente: 'Benvenuto nel Team RememberMe Live',
    email_template_registrazione_agente: '',
    // Template Email - Reset Password
    email_oggetto_reset_password: 'Reset Password - RememberMe Live',
    email_template_reset_password: '',
    // Template Email - Transazionali
    email_oggetto_pagamento_completato: 'Conferma Pagamento - RememberMe Live',
    email_template_pagamento_completato: '',
    email_oggetto_pubblicazione_elogio: 'Elogio Pubblicato - RememberMe Live',
    email_template_pubblicazione_elogio: '',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error('Errore nel caricamento impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Impostazioni salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Inserisci un\'email di test');
      return;
    }
    setTestingEmail(true);
    try {
      await testEmail(testEmailAddress);
      toast.success('Email di test inviata!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingFavicon(true);
    try {
      const response = await uploadFavicon(file);
      setSettings({...settings, favicon_file_id: response.data.file_id});
      toast.success('Favicon caricata con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore caricamento favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleResetTemplates = async () => {
    if (!window.confirm('Sei sicuro di voler resettare tutti i template email a versioni in testo semplice? I template attuali verranno sovrascritti.')) {
      return;
    }
    setResettingTemplates(true);
    try {
      await resetEmailTemplates();
      toast.success('Template email resettati! Ricarico la pagina...');
      // Ricarica le impostazioni
      const response = await getSettings();
      setSettings(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore reset template');
    } finally {
      setResettingTemplates(false);
    }
  };

  // Funzioni gestione QR
  const handleLoadDiagnostica = async () => {
    setLoadingDiagnostica(true);
    try {
      const response = await getDiagnosticaQR();
      setQrDiagnostica(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore caricamento diagnostica');
    } finally {
      setLoadingDiagnostica(false);
    }
  };

  const handlePulisciOrfani = async () => {
    if (!window.confirm('Sei sicuro di voler rimuovere tutti i QR code orfani (aziende non esistenti)?')) return;
    setPulendoOrfani(true);
    try {
      const response = await pulisciQROrfani();
      toast.success(`Rimossi ${response.data.qr_rimossi} QR code orfani`);
      handleLoadDiagnostica(); // Ricarica diagnostica
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore pulizia QR');
    } finally {
      setPulendoOrfani(false);
    }
  };

  const handleRimuoviRange = async () => {
    const start = parseInt(qrRangeToRemove.start);
    const end = parseInt(qrRangeToRemove.end);
    if (isNaN(start) || isNaN(end) || start > end) {
      toast.error('Range non valido');
      return;
    }
    if (!window.confirm(`Sei sicuro di voler rimuovere i QR code dal ${start} al ${end}? Questa azione è irreversibile.`)) return;
    
    setRimuovendoRange(true);
    try {
      const response = await rimuoviQRPerRange(start, end);
      if (response.data.success) {
        toast.success(`Rimossi ${response.data.qr_rimossi} QR code`);
        setQrRangeToRemove({ start: '', end: '' });
        handleLoadDiagnostica(); // Ricarica diagnostica
      } else {
        toast.error(response.data.error || 'Impossibile rimuovere: ci sono QR attivi');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore rimozione QR');
    } finally {
      setRimuovendoRange(false);
    }
  };

  const handleDebugElogio = async () => {
    if (!debugElogioId.trim()) {
      toast.error('Inserisci l\'ID dell\'elogio');
      return;
    }
    setLoadingDebug(true);
    setDebugResult(null);
    try {
      const response = await debugElogio(debugElogioId.trim());
      setDebugResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore debug');
    } finally {
      setLoadingDebug(false);
    }
  };

  const handleCollegaAzienda = async () => {
    if (!aziendaDaCollegare.trim() || !debugElogioId.trim()) {
      toast.error('Inserisci ID azienda');
      return;
    }
    setCollegandoAzienda(true);
    try {
      const response = await collegaElogioAzienda(debugElogioId.trim(), aziendaDaCollegare.trim());
      toast.success(response.data.message);
      setAziendaDaCollegare('');
      // Ricarica debug
      handleDebugElogio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore collegamento');
    } finally {
      setCollegandoAzienda(false);
    }
  };

  const handleDiagnosticaAzienda = async () => {
    if (!diagnosticaAziendaId.trim()) {
      toast.error('Inserisci ID azienda (es. AZ_XXXXXXXX)');
      return;
    }
    setLoadingDiagnosticaAzienda(true);
    try {
      const response = await diagnosticaAziendaElogi(diagnosticaAziendaId.trim());
      setDiagnosticaAziendaResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore diagnostica azienda');
      setDiagnosticaAziendaResult(null);
    } finally {
      setLoadingDiagnosticaAzienda(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="impostazioni-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Impostazioni
          </h1>
          <p className="text-sm text-slate-500 mt-1">Configura il sistema</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salva
        </Button>
      </div>

      <Tabs defaultValue="generale" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 lg:grid-cols-5 h-auto gap-1">
          <TabsTrigger value="generale" className="flex items-center gap-2 text-xs sm:text-sm py-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Generale</span>
          </TabsTrigger>
          <TabsTrigger value="pagamenti" className="flex items-center gap-2 text-xs sm:text-sm py-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Pagamenti</span>
          </TabsTrigger>
          <TabsTrigger value="integrazioni" className="flex items-center gap-2 text-xs sm:text-sm py-2">
            <Link className="h-4 w-4" />
            <span className="hidden sm:inline">Integrazioni</span>
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2 text-xs sm:text-sm py-2">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Email</span>
          </TabsTrigger>
          <TabsTrigger value="database" className="flex items-center gap-2 text-xs sm:text-sm py-2">
            <QrCode className="h-4 w-4" />
            <span className="hidden sm:inline">Database</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB GENERALE */}
        <TabsContent value="generale" className="space-y-6">
          {/* URL Frontend */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-100">
                  <Globe className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Configurazione Base</CardTitle>
                  <CardDescription>URL e impostazioni generali del sistema</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="frontend_url">URL Frontend</Label>
                <Input
                  id="frontend_url"
                  placeholder="https://remembermev2-production.up.railway.app"
                  value={settings.frontend_url || ''}
                  onChange={(e) => setSettings({...settings, frontend_url: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">URL pubblico dell'applicazione (per redirect e link nelle email)</p>
              </div>
              
              <div>
                <Label htmlFor="api_url">URL API (opzionale)</Label>
                <Input
                  id="api_url"
                  placeholder="https://api.tuodominio.it"
                  value={settings.api_url || ''}
                  onChange={(e) => setSettings({...settings, api_url: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">
                  URL dove le API sono raggiungibili pubblicamente (per Wix e servizi esterni). 
                  Se non configurato, verrà usato l'URL Frontend.
                </p>
              </div>
              
              {/* Favicon Upload */}
              <div>
                <Label>Favicon</Label>
                <div className="flex items-center gap-4 mt-2">
                  {settings.favicon_file_id && (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${settings.favicon_file_id}`}
                      alt="Favicon attuale"
                      className="w-12 h-12 object-contain border rounded"
                    />
                  )}
                  <label className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-slate-50 transition-colors">
                      {uploadingFavicon ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      <span>{settings.favicon_file_id ? 'Cambia Favicon' : 'Carica Favicon'}</span>
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleFaviconUpload}
                      disabled={uploadingFavicon}
                    />
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-1">Immagine .ico, .png o .svg (consigliato 32x32 o 64x64 pixel)</p>
              </div>
            </CardContent>
          </Card>

          {/* Prezzi */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <Euro className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Prezzi</CardTitle>
                  <CardDescription>Configurazione prezzi per attivazione QR</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="prezzo_base">Prezzo Base (€)</Label>
                <Input
                  id="prezzo_base"
                  type="number"
                  value={settings.prezzo_base || ''}
                  onChange={(e) => setSettings({...settings, prezzo_base: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label htmlFor="prezzo_premium">Prezzo Premium (€)</Label>
                <Input
                  id="prezzo_premium"
                  type="number"
                  value={settings.prezzo_premium || ''}
                  onChange={(e) => setSettings({...settings, prezzo_premium: parseFloat(e.target.value)})}
                />
              </div>
            </CardContent>
          </Card>

          {/* OpenAI */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <Brain className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">OpenAI API (GPT-4)</CardTitle>
                  <CardDescription>Chiave API per generazione testi con intelligenza artificiale</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="openai_api_key">API Key</Label>
                <Input
                  id="openai_api_key"
                  type="password"
                  placeholder="sk-..."
                  value={settings.openai_api_key || ''}
                  onChange={(e) => setSettings({...settings, openai_api_key: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Ottieni la chiave da <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">platform.openai.com</a>
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium text-slate-700">Sistema AI in uso:</p>
                <p className="text-slate-600">GPT-4 (OpenAI) tramite emergentintegrations</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB PAGAMENTI */}
        <TabsContent value="pagamenti" className="space-y-6">
          {/* Stripe */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Stripe</CardTitle>
                  <CardDescription>Configurazione pagamenti con carta di credito</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="stripe_public_key">Public Key</Label>
                <Input
                  id="stripe_public_key"
                  placeholder="pk_..."
                  value={settings.stripe_public_key || ''}
                  onChange={(e) => setSettings({...settings, stripe_public_key: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="stripe_secret_key">Secret Key</Label>
                <Input
                  id="stripe_secret_key"
                  type="password"
                  placeholder="sk_..."
                  value={settings.stripe_secret_key || ''}
                  onChange={(e) => setSettings({...settings, stripe_secret_key: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="stripe_webhook_secret">Webhook Secret</Label>
                <Input
                  id="stripe_webhook_secret"
                  type="password"
                  placeholder="whsec_..."
                  value={settings.stripe_webhook_secret || ''}
                  onChange={(e) => setSettings({...settings, stripe_webhook_secret: e.target.value})}
                />
              </div>
            </CardContent>
          </Card>

          {/* PayPal */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Wallet className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">PayPal</CardTitle>
                  <CardDescription>Configurazione pagamenti PayPal</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="paypal_client_id">Client ID</Label>
                <Input
                  id="paypal_client_id"
                  placeholder="Client ID"
                  value={settings.paypal_client_id || ''}
                  onChange={(e) => setSettings({...settings, paypal_client_id: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="paypal_client_secret">Client Secret</Label>
                <Input
                  id="paypal_client_secret"
                  type="password"
                  placeholder="Client Secret"
                  value={settings.paypal_client_secret || ''}
                  onChange={(e) => setSettings({...settings, paypal_client_secret: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="paypal_mode">Modalità</Label>
                <Select
                  value={settings.paypal_mode || 'sandbox'}
                  onValueChange={(value) => setSettings({...settings, paypal_mode: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona modalità" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (Test)</SelectItem>
                    <SelectItem value="live">Live (Produzione)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB INTEGRAZIONI */}
        <TabsContent value="integrazioni" className="space-y-6">
          {/* Wix */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Globe className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Wix Integration</CardTitle>
                  <CardDescription>Sincronizzazione automatica con il sito Wix</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="wix_bearer_token">Bearer Token</Label>
                <Input
                  id="wix_bearer_token"
                  type="password"
                  placeholder="IST.eyJ..."
                  value={settings.wix_bearer_token || ''}
                  onChange={(e) => setSettings({...settings, wix_bearer_token: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="wix_site_id">Site ID</Label>
                <Input
                  id="wix_site_id"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={settings.wix_site_id || ''}
                  onChange={(e) => setSettings({...settings, wix_site_id: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="wix_collection">Nome Collection</Label>
                <Input
                  id="wix_collection"
                  placeholder="elogi"
                  value={settings.wix_collection || ''}
                  onChange={(e) => setSettings({...settings, wix_collection: e.target.value})}
                />
              </div>
            </CardContent>
          </Card>

          {/* Resend Email API */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-sky-100">
                  <Mail className="h-5 w-5 text-sky-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Email (Resend)</CardTitle>
                  <CardDescription>Configurazione per invio email automatiche via Resend</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="text-blue-800">
                  <strong>Resend</strong> è il servizio per l'invio email. 
                  Ottieni la tua API key su <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a>
                </p>
              </div>
              <div>
                <Label htmlFor="resend_api_key">API Key Resend</Label>
                <Input
                  id="resend_api_key"
                  type="password"
                  placeholder="re_xxxxxxxxx"
                  value={settings.resend_api_key || ''}
                  onChange={(e) => setSettings({...settings, resend_api_key: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">Inizia con "re_"</p>
              </div>
              <div>
                <Label htmlFor="email_from">Email Mittente</Label>
                <Input
                  id="email_from"
                  placeholder="noreply@remembermelive.it"
                  value={settings.email_from || ''}
                  onChange={(e) => setSettings({...settings, email_from: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">Deve essere un dominio verificato su Resend</p>
              </div>
              
              {/* Test Email */}
              <div className="pt-4 border-t">
                <Label className="mb-2 block">Test Invio Email</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="email@test.com"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                  />
                  <Button 
                    onClick={handleTestEmail} 
                    disabled={testingEmail}
                    variant="outline"
                  >
                    {testingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Salva prima di testare</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB EMAIL TEMPLATES */}
        <TabsContent value="email" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Template Email</CardTitle>
                  <CardDescription>Personalizza oggetto e contenuto delle email automatiche</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="p-3 bg-blue-50 rounded-lg text-sm mb-6">
                <p className="font-medium text-blue-800">Variabili disponibili:</p>
                <p className="text-blue-700 mt-1">
                  <code className="bg-blue-100 px-1 rounded">{"{{nome}}"}</code>, 
                  <code className="bg-blue-100 px-1 rounded ml-1">{"{{cognome}}"}</code>, 
                  <code className="bg-blue-100 px-1 rounded ml-1">{"{{email}}"}</code>, 
                  <code className="bg-blue-100 px-1 rounded ml-1">{"{{password}}"}</code>, 
                  <code className="bg-blue-100 px-1 rounded ml-1">{"{{link_login}}"}</code>, 
                  <code className="bg-blue-100 px-1 rounded ml-1">{"{{reset_link}}"}</code>, 
                  <code className="bg-blue-100 px-1 rounded ml-1">{"{{link_elogio}}"}</code>, 
                  <code className="bg-blue-100 px-1 rounded ml-1">{"{{nome_defunto}}"}</code>
                </p>
              </div>

              {/* Pulsante Reset Templates */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleResetTemplates}
                  disabled={resettingTemplates}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  {resettingTemplates ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  Resetta Template (Testo Semplice)
                </Button>
              </div>

              <div className="space-y-8">
                {/* Registrazione Azienda */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Registrazione Azienda
                  </h4>
                  <div>
                    <Label htmlFor="email_oggetto_registrazione_azienda">Oggetto</Label>
                    <Input
                      id="email_oggetto_registrazione_azienda"
                      placeholder="Benvenuto su RememberMe Live"
                      value={settings.email_oggetto_registrazione_azienda || ''}
                      onChange={(e) => setSettings({...settings, email_oggetto_registrazione_azienda: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Contenuto Email</Label>
                    <Textarea
                      value={settings.email_template_registrazione_azienda || ''}
                      onChange={(e) => setSettings({...settings, email_template_registrazione_azienda: e.target.value})}
                      placeholder="Scrivi il contenuto dell'email... Usa le variabili {{nome}}, {{email}}, {{password}}, {{link_login}}"
                      rows={6}
                      className="resize-none"
                    />
                    <p className="text-xs text-slate-500 mt-1">Usa {"{{password}}"} per inserire la password generata</p>
                  </div>
                </div>

                {/* Registrazione Agente */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Registrazione Agente
                  </h4>
                  <div>
                    <Label htmlFor="email_oggetto_registrazione_agente">Oggetto</Label>
                    <Input
                      id="email_oggetto_registrazione_agente"
                      placeholder="Benvenuto nel Team RememberMe Live"
                      value={settings.email_oggetto_registrazione_agente || ''}
                      onChange={(e) => setSettings({...settings, email_oggetto_registrazione_agente: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Contenuto Email</Label>
                    <Textarea
                      value={settings.email_template_registrazione_agente || ''}
                      onChange={(e) => setSettings({...settings, email_template_registrazione_agente: e.target.value})}
                      placeholder="Scrivi il contenuto dell'email... Usa le variabili {{nome}}, {{email}}, {{password}}, {{link_login}}"
                      rows={6}
                      className="resize-none"
                    />
                    <p className="text-xs text-slate-500 mt-1">Usa {"{{password}}"} per inserire la password generata</p>
                  </div>
                </div>

                {/* Registrazione Admin */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    Registrazione Admin
                  </h4>
                  <div>
                    <Label htmlFor="email_oggetto_registrazione_admin">Oggetto</Label>
                    <Input
                      id="email_oggetto_registrazione_admin"
                      placeholder="Account Admin Creato - RememberMe Live"
                      value={settings.email_oggetto_registrazione_admin || ''}
                      onChange={(e) => setSettings({...settings, email_oggetto_registrazione_admin: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Contenuto Email</Label>
                    <Textarea
                      value={settings.email_template_registrazione_admin || ''}
                      onChange={(e) => setSettings({...settings, email_template_registrazione_admin: e.target.value})}
                      placeholder="Scrivi il contenuto dell'email..."
                      rows={6}
                      className="resize-none"
                    />
                  </div>
                </div>

                {/* Reset Password */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Reset Password
                  </h4>
                  <div>
                    <Label htmlFor="email_oggetto_reset_password">Oggetto</Label>
                    <Input
                      id="email_oggetto_reset_password"
                      placeholder="Reset Password - RememberMe Live"
                      value={settings.email_oggetto_reset_password || ''}
                      onChange={(e) => setSettings({...settings, email_oggetto_reset_password: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Contenuto Email</Label>
                    <Textarea
                      value={settings.email_template_reset_password || ''}
                      onChange={(e) => setSettings({...settings, email_template_reset_password: e.target.value})}
                      placeholder="Usa {{reset_link}} per il link di reset"
                      rows={6}
                      className="resize-none"
                    />
                  </div>
                </div>

                {/* Pagamento Completato */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    Pagamento Completato
                  </h4>
                  <div>
                    <Label htmlFor="email_oggetto_pagamento_completato">Oggetto</Label>
                    <Input
                      id="email_oggetto_pagamento_completato"
                      placeholder="Conferma Pagamento - RememberMe Live"
                      value={settings.email_oggetto_pagamento_completato || ''}
                      onChange={(e) => setSettings({...settings, email_oggetto_pagamento_completato: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Contenuto Email</Label>
                    <Textarea
                      value={settings.email_template_pagamento_completato || ''}
                      onChange={(e) => setSettings({...settings, email_template_pagamento_completato: e.target.value})}
                      placeholder="Usa {{importo}}, {{data}}, {{nome_defunto}}"
                      rows={6}
                      className="resize-none"
                    />
                  </div>
                </div>

                {/* Pubblicazione Elogio */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <span className="w-2 h-2 bg-sky-500 rounded-full"></span>
                    Pubblicazione Elogio
                  </h4>
                  <div>
                    <Label htmlFor="email_oggetto_pubblicazione_elogio">Oggetto</Label>
                    <Input
                      id="email_oggetto_pubblicazione_elogio"
                      placeholder="Elogio Pubblicato - RememberMe Live"
                      value={settings.email_oggetto_pubblicazione_elogio || ''}
                      onChange={(e) => setSettings({...settings, email_oggetto_pubblicazione_elogio: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Contenuto Email</Label>
                    <Textarea
                      value={settings.email_template_pubblicazione_elogio || ''}
                      onChange={(e) => setSettings({...settings, email_template_pubblicazione_elogio: e.target.value})}
                      placeholder="Usa {{link_elogio}}, {{nome_defunto}} - Include PDF QR Code in allegato"
                      rows={6}
                      className="resize-none"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB DATABASE */}
        <TabsContent value="database" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="h-5 w-5" />
                Gestione QR Code Database
              </CardTitle>
              <CardDescription>
                Diagnostica e pulizia dei QR code nel database
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Diagnostica */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Diagnostica QR Code</h3>
                  <Button 
                    onClick={handleLoadDiagnostica} 
                    disabled={loadingDiagnostica}
                    variant="outline"
                    size="sm"
                  >
                    {loadingDiagnostica ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Search className="h-4 w-4 mr-2" />
                    )}
                    Analizza Database
                  </Button>
                </div>

                {qrDiagnostica && (
                  <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-slate-900">{qrDiagnostica.totale_qr}</p>
                        <p className="text-xs text-slate-500">QR Totali</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{qrDiagnostica.aziende_valide?.length || 0}</p>
                        <p className="text-xs text-slate-500">Aziende Valide</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className={`text-2xl font-bold ${qrDiagnostica.qr_orfani > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {qrDiagnostica.qr_orfani}
                        </p>
                        <p className="text-xs text-slate-500">QR Orfani</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">
                          {Object.keys(qrDiagnostica.orfani_per_azienda || {}).length}
                        </p>
                        <p className="text-xs text-slate-500">Aziende Fantasma</p>
                      </div>
                    </div>

                    {/* QR per Azienda Valida */}
                    {qrDiagnostica.qr_per_azienda_valida && Object.keys(qrDiagnostica.qr_per_azienda_valida).length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">QR per Azienda:</h4>
                        <div className="space-y-2">
                          {Object.entries(qrDiagnostica.qr_per_azienda_valida).map(([azienda, ids]) => (
                            <div key={azienda} className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline">{azienda}</Badge>
                              <span className="text-sm text-slate-600">
                                {ids.length} QR: {ids.length > 10 
                                  ? `${Math.min(...ids)}-${Math.max(...ids)}` 
                                  : ids.join(', ')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* QR Orfani */}
                    {qrDiagnostica.qr_orfani > 0 && (
                      <div className="border-t pt-4">
                        <h4 className="font-medium text-sm mb-2 text-red-600">⚠️ QR Orfani (da aziende eliminate):</h4>
                        <div className="space-y-2 mb-4">
                          {Object.entries(qrDiagnostica.orfani_per_azienda || {}).map(([azienda, ids]) => (
                            <div key={azienda} className="flex items-center gap-2 flex-wrap">
                              <Badge variant="destructive">{azienda}</Badge>
                              <span className="text-sm text-slate-600">
                                {ids.length} QR: {ids.length > 10 
                                  ? `${Math.min(...ids)}-${Math.max(...ids)}` 
                                  : ids.join(', ')}
                              </span>
                            </div>
                          ))}
                        </div>
                        <Button 
                          onClick={handlePulisciOrfani} 
                          disabled={pulendoOrfani}
                          variant="destructive"
                          size="sm"
                        >
                          {pulendoOrfani ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Trash2 className="h-4 w-4 mr-2" />
                          )}
                          Pulisci {qrDiagnostica.qr_orfani} QR Orfani
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Rimozione Range Manuale */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">Rimozione QR per Range</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Rimuovi manualmente un range di QR code. I QR attivi o collegati a elogi non verranno rimossi.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label htmlFor="qr_range_start">Da ID</Label>
                    <Input
                      id="qr_range_start"
                      type="number"
                      placeholder="Es: 1"
                      value={qrRangeToRemove.start}
                      onChange={(e) => setQrRangeToRemove({...qrRangeToRemove, start: e.target.value})}
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="qr_range_end">A ID</Label>
                    <Input
                      id="qr_range_end"
                      type="number"
                      placeholder="Es: 100"
                      value={qrRangeToRemove.end}
                      onChange={(e) => setQrRangeToRemove({...qrRangeToRemove, end: e.target.value})}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleRimuoviRange} 
                      disabled={rimuovendoRange || !qrRangeToRemove.start || !qrRangeToRemove.end}
                      variant="destructive"
                    >
                      {rimuovendoRange ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      Rimuovi
                    </Button>
                  </div>
                </div>
              </div>

              {/* Debug Elogio */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">🔍 Debug Elogio</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Verifica lo stato di un elogio e i QR code associati all'azienda.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <div className="flex-1">
                    <Label htmlFor="debug_elogio_id">ID Elogio</Label>
                    <Input
                      id="debug_elogio_id"
                      placeholder="Es: ID-FF10DABB"
                      value={debugElogioId}
                      onChange={(e) => setDebugElogioId(e.target.value)}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleDebugElogio} 
                      disabled={loadingDebug}
                      variant="outline"
                    >
                      {loadingDebug ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Search className="h-4 w-4 mr-2" />
                      )}
                      Verifica
                    </Button>
                  </div>
                </div>

                {debugResult && (
                  <div className="border rounded-lg p-4 bg-slate-50 space-y-4">
                    {debugResult.error ? (
                      <p className="text-red-600">{debugResult.error}</p>
                    ) : (
                      <>
                        {/* Info Elogio */}
                        <div>
                          <h4 className="font-medium text-sm mb-2">📄 Elogio</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <span className="text-slate-500">ID:</span>
                            <span className="font-mono">{debugResult.elogio?.id}</span>
                            <span className="text-slate-500">Nome:</span>
                            <span>{debugResult.elogio?.nome} {debugResult.elogio?.cognome}</span>
                            <span className="text-slate-500">ID Azienda:</span>
                            <span className={`font-mono font-bold ${!debugResult.elogio?.id_azienda ? 'text-red-600' : ''}`}>
                              {debugResult.elogio?.id_azienda || '⚠️ NON COLLEGATO'}
                            </span>
                            <span className="text-slate-500">Pubblicato:</span>
                            <span>{debugResult.elogio?.pubblicato ? '✅ Sì' : '❌ No'}</span>
                            <span className="text-slate-500">Elaborato:</span>
                            <span>{debugResult.elogio?.processed === 'YES' ? '✅ Sì' : '❌ No'}</span>
                          </div>
                          
                          {/* Form collegamento azienda se non collegato */}
                          {!debugResult.elogio?.id_azienda && (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800 mb-2 font-medium">⚠️ Questo elogio non è collegato a nessuna azienda!</p>
                              <div className="flex gap-2">
                                <Input
                                  placeholder="ID Azienda (es: AZ008)"
                                  value={aziendaDaCollegare}
                                  onChange={(e) => setAziendaDaCollegare(e.target.value)}
                                  className="flex-1"
                                />
                                <Button 
                                  onClick={handleCollegaAzienda}
                                  disabled={collegandoAzienda}
                                  size="sm"
                                >
                                  {collegandoAzienda ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    'Collega'
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Info Azienda */}
                        <div className="border-t pt-4">
                          <h4 className="font-medium text-sm mb-2">🏢 Azienda</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <span className="text-slate-500">Esiste:</span>
                            <span>{debugResult.azienda?.esiste ? '✅ Sì' : '❌ No'}</span>
                            <span className="text-slate-500">ID Azienda:</span>
                            <span className="font-mono">{debugResult.azienda?.id_azienda || 'N/A'}</span>
                            <span className="text-slate-500">Ragione Sociale:</span>
                            <span>{debugResult.azienda?.ragione_sociale || 'N/A'}</span>
                          </div>
                        </div>

                        {/* Info QR */}
                        <div className="border-t pt-4">
                          <h4 className="font-medium text-sm mb-2">📱 QR Code</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                            <span className="text-slate-500">QR Totali Azienda:</span>
                            <span className={`font-bold ${debugResult.qr_totali_azienda > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {debugResult.qr_totali_azienda}
                            </span>
                            <span className="text-slate-500">QR Liberi:</span>
                            <span className={`font-bold ${debugResult.qr_liberi > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {debugResult.qr_liberi}
                            </span>
                          </div>
                          
                          {debugResult.dettaglio_qr?.length > 0 && (
                            <div className="text-xs bg-white p-2 rounded border max-h-40 overflow-y-auto">
                              <p className="font-medium mb-1">Dettaglio QR:</p>
                              {debugResult.dettaglio_qr.map((qr, idx) => (
                                <div key={idx} className="flex gap-2 py-1 border-b last:border-0">
                                  <span>ID: {qr.id}</span>
                                  <span>|</span>
                                  <span>Azienda: {qr.id_azienda}</span>
                                  <span>|</span>
                                  <span>Elogio: {qr.id_elogio || '(libero)'}</span>
                                  <span>|</span>
                                  <span>Attivo: {qr.attivo ? '✅' : '❌'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Diagnostica Azienda */}
              <div className="border-t pt-6">
                <h3 className="text-base font-medium text-slate-900 mb-2 flex items-center gap-2">
                  🏢 Diagnostica Elogi Azienda
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Verifica perché un'azienda non vede i suoi elogi. Inserisci l'ID azienda.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <div className="flex-1">
                    <Label htmlFor="diagnostica_azienda_id">ID Azienda</Label>
                    <Input
                      id="diagnostica_azienda_id"
                      placeholder="Es: AZ_XXXXXXXX"
                      value={diagnosticaAziendaId}
                      onChange={(e) => setDiagnosticaAziendaId(e.target.value)}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleDiagnosticaAzienda} 
                      disabled={loadingDiagnosticaAzienda}
                      variant="outline"
                    >
                      {loadingDiagnosticaAzienda ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Search className="h-4 w-4 mr-2" />
                      )}
                      Analizza
                    </Button>
                  </div>
                </div>

                {diagnosticaAziendaResult && (
                  <div className="border rounded-lg p-4 bg-slate-50 space-y-4">
                    {diagnosticaAziendaResult.error ? (
                      <p className="text-red-600">{diagnosticaAziendaResult.error}</p>
                    ) : (
                      <>
                        {/* Info Azienda */}
                        <div>
                          <h4 className="font-medium text-sm mb-2">🏢 Azienda</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <span className="text-slate-500">ID:</span>
                            <span className="font-mono">{diagnosticaAziendaResult.azienda?.id_azienda}</span>
                            <span className="text-slate-500">Ragione Sociale:</span>
                            <span>{diagnosticaAziendaResult.azienda?.ragione_sociale}</span>
                            <span className="text-slate-500">Email:</span>
                            <span>{diagnosticaAziendaResult.azienda?.email}</span>
                          </div>
                        </div>

                        {/* Info User */}
                        <div className="border-t pt-4">
                          <h4 className="font-medium text-sm mb-2">👤 Utente Collegato</h4>
                          {diagnosticaAziendaResult.user_collegato ? (
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <span className="text-slate-500">ID:</span>
                              <span className="font-mono">{diagnosticaAziendaResult.user_collegato.id}</span>
                              <span className="text-slate-500">Email:</span>
                              <span>{diagnosticaAziendaResult.user_collegato.email}</span>
                              <span className="text-slate-500">Ruolo:</span>
                              <span>{diagnosticaAziendaResult.user_collegato.ruolo}</span>
                              <span className="text-slate-500">ID Azienda (user):</span>
                              <span className={`font-mono font-bold ${diagnosticaAziendaResult.user_collegato.id_azienda !== diagnosticaAziendaResult.azienda?.id_azienda ? 'text-red-600' : 'text-green-600'}`}>
                                {diagnosticaAziendaResult.user_collegato.id_azienda || '⚠️ MANCANTE'}
                              </span>
                            </div>
                          ) : (
                            <p className="text-red-600 text-sm">⚠️ Nessun utente collegato a questa azienda!</p>
                          )}
                        </div>

                        {/* Statistiche Elogi */}
                        <div className="border-t pt-4">
                          <h4 className="font-medium text-sm mb-2">📊 Statistiche Elogi</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <span className="text-slate-500">Per campo 'origine':</span>
                            <span className={`font-bold ${diagnosticaAziendaResult.statistiche_elogi?.per_campo_origine === 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {diagnosticaAziendaResult.statistiche_elogi?.per_campo_origine}
                            </span>
                            <span className="text-slate-500">Per campo 'id_azienda':</span>
                            <span>{diagnosticaAziendaResult.statistiche_elogi?.per_campo_id_azienda}</span>
                            <span className="text-slate-500">Di cui demo (nascosti):</span>
                            <span className={diagnosticaAziendaResult.statistiche_elogi?.di_cui_demo > 0 ? 'text-amber-600' : ''}>
                              {diagnosticaAziendaResult.statistiche_elogi?.di_cui_demo}
                            </span>
                            <span className="text-slate-500">Visibili all'azienda:</span>
                            <span className="font-bold text-blue-600">
                              {diagnosticaAziendaResult.statistiche_elogi?.non_demo_visibili}
                            </span>
                            <span className="text-slate-500">QR Assegnati:</span>
                            <span>{diagnosticaAziendaResult.qr_assegnati}</span>
                          </div>
                        </div>

                        {/* Dettaglio Elogi */}
                        {diagnosticaAziendaResult.dettaglio_elogi?.length > 0 && (
                          <div className="border-t pt-4">
                            <h4 className="font-medium text-sm mb-2">📄 Dettaglio Elogi (max 20)</h4>
                            <div className="max-h-60 overflow-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-100">
                                  <tr>
                                    <th className="p-2 text-left">ID</th>
                                    <th className="p-2 text-left">Nome</th>
                                    <th className="p-2 text-left">Origine</th>
                                    <th className="p-2 text-left">Demo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {diagnosticaAziendaResult.dettaglio_elogi.map((e, idx) => (
                                    <tr key={idx} className="border-t">
                                      <td className="p-2 font-mono">{e.id}</td>
                                      <td className="p-2">{e.nome} {e.cognome}</td>
                                      <td className="p-2 font-mono text-xs">{e.origine || '-'}</td>
                                      <td className="p-2">{e.is_demo ? '⚠️ Sì' : '✅ No'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Note */}
                        <div className="border-t pt-4">
                          <p className="text-xs text-slate-500 bg-amber-50 p-2 rounded">
                            💡 {diagnosticaAziendaResult.note}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImpostazioniPage;
